import { Check, ChevronLeft, ChevronRight, Download, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import { ActionInfo, Button, StatusMessage } from '@krp/ui-kit'

import {
  EXPORT_FORMATS,
  FILTER_LABELS,
  PAIR_FILTERS,
  counterOf,
  type DuplicatesActions,
  type DuplicatesVm,
  type ExportFormat,
  type ExportScope,
  type ScanActions,
  type ScanVm,
} from '../model/types'
import { ErrorBlock } from './ErrorBlock'
import { PairRow } from './PairRow'
import { ScanPanel } from './ScanPanel'
import './duplicates.css'

const FORMAT_LABELS: Readonly<Record<ExportFormat, string>> = { xlsx: 'Excel', json: 'JSON', txt: 'TXT', xml: 'XML' }

function Filters({ vm, actions }: { vm: DuplicatesVm; actions: DuplicatesActions }) {
  return (
    <div className="dup-filters" role="group" aria-label="Какие пары показывать">
      {PAIR_FILTERS.map((filter) => (
        <ActionInfo key={filter} id="duplicates.source.filter" label={FILTER_LABELS[filter]}>
          <Button
            variant={vm.filter === filter ? 'primary' : 'secondary'}
            icon={vm.filter === filter ? <Check size={16} aria-hidden="true" /> : undefined}
            aria-pressed={vm.filter === filter}
            onClick={() => actions.filterChanged(filter)}
          >
            {FILTER_LABELS[filter]}{' '}
            <span className="dup-count">{counterOf(vm.counters, filter)}</span>
          </Button>
        </ActionInfo>
      ))}
    </div>
  )
}

/** «Выгрузить» (И-10): объём и вид файла; файл готовит и отдаёт служба, экран говорит, что происходит сейчас. */
function ExportPanel({ vm, actions }: { vm: DuplicatesVm; actions: DuplicatesActions }) {
  const [scope, setScope] = useState<ExportScope>('marked')
  const { exportView } = vm
  const busy = exportView.phase === 'preparing'
  const reason = vm.hasScan ? null : 'Сначала запустите проверку: выгружать пока нечего.'
  return (
    <section className="dup__panel dup-export" aria-label="Выгрузка списка">
      <p className="dup__panel-title">Выгрузить</p>
      <div className="dup-export__group" role="group" aria-label="Что выгружать">
        <Button variant={scope === 'marked' ? 'primary' : 'secondary'} aria-pressed={scope === 'marked'} onClick={() => setScope('marked')} actionId="duplicates.source.export-scope">
          Только отмеченные
        </Button>
        <Button variant={scope === 'all' ? 'primary' : 'secondary'} aria-pressed={scope === 'all'} onClick={() => setScope('all')} actionId="duplicates.source.export-scope">
          Весь список
        </Button>
      </div>
      <div className="dup-export__group" role="group" aria-label="В каком виде">
        {EXPORT_FORMATS.map((format) => (
          <Button
            key={format}
            variant="secondary"
            icon={<Download size={16} aria-hidden="true" />}
            busy={busy}
            disabled={reason !== null}
            disabledReason={reason}
            onClick={() => actions.exportFile(format, scope)}
            actionId="duplicates.source.export-file"
          >
            {FORMAT_LABELS[format]}
          </Button>
        ))}
      </div>
      {exportView.phase === 'preparing' && (
        <p role="status" className="dup__note">
          Готовим файл…
        </p>
      )}
      {exportView.phase === 'saved' && exportView.fileName !== null && (
        <p role="status" className="dup__note">
          Файл «{exportView.fileName}» передан браузеру на сохранение.
        </p>
      )}
      {exportView.phase === 'saved' && exportView.fileName === null && (
        <p role="status" className="dup__note">
          Файл передан браузеру на сохранение.
        </p>
      )}
      {exportView.error !== null && <ErrorBlock error={exportView.error} />}
    </section>
  )
}

function emptyText(vm: DuplicatesVm): string {
  if (!vm.hasScan) return 'Проверка по этому источнику ещё не запускалась: нажмите «Найти дубли».'
  if (vm.filter === 'not-duplicate') return 'Пар, отмеченных «Не дубли», нет.'
  if (vm.filter === 'marked') return 'Отмеченных пар пока нет.'
  if (vm.filter === 'undecided') return 'Неразобранных пар нет.'
  return 'Все пары разобраны или проверка ничего похожего не нашла.'
}

/**
 * Раздел «Дубли» одного источника (RP-DUP8): панель проверки, фильтры со счётчиками, список пар, выгрузка. Данные приходят
 * готовыми (vm) вместе с действиями; экран ничего не запрашивает сам. Состояния различаются словом, значком и рамкой.
 */
export function DuplicatesScreen({ scan, list }: { scan: { vm: ScanVm; actions: ScanActions }; list: { vm: DuplicatesVm; actions: DuplicatesActions } }) {
  const { vm, actions } = list
  return (
    <section aria-labelledby="duplicates-title" className="dup">
      <header>
        <h2 id="duplicates-title">Дубли</h2>
        <p className="dup__lead">Здесь собраны пары товаров этого источника, которые, скорее всего, один и тот же товар. Сначала запустите поиск, затем разберите пары: ничего не удаляется и не объединяется само, решаете вы.</p>
      </header>

      <ScanPanel vm={scan.vm} actions={scan.actions} />

      <Filters vm={vm} actions={actions} />

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем список пар…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="duplicates.source.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' && (
        <>
          <p className="dup__note" aria-live="polite">
            Пар в списке: {vm.total}
            {vm.refreshing && ' · обновляется…'}
          </p>
          {vm.pairs.length === 0 ? (
            <StatusMessage kind="empty" title={emptyText(vm)} />
          ) : (
            <ul className="dup-list" aria-label="Пары товаров">
              {vm.pairs.map((view) => (
                <PairRow key={view.pair.id} view={view} actions={actions} />
              ))}
            </ul>
          )}
          {vm.pages > 1 && (
            <nav className="dup-pager" aria-label="Страницы пар">
              <Button variant="secondary" icon={<ChevronLeft size={16} aria-hidden="true" />} disabled={vm.page <= 1} disabledReason={vm.page <= 1 ? 'Это первая страница.' : null} onClick={() => actions.pageChanged(vm.page - 1)} actionId="duplicates.source.page">
                Назад
              </Button>
              <span aria-current="page">
                Страница {vm.page} из {vm.pages}
              </span>
              <Button variant="secondary" icon={<ChevronRight size={16} aria-hidden="true" />} disabled={vm.page >= vm.pages} disabledReason={vm.page >= vm.pages ? 'Это последняя страница.' : null} onClick={() => actions.pageChanged(vm.page + 1)} actionId="duplicates.source.page">
                Дальше
              </Button>
            </nav>
          )}
        </>
      )}

      <ExportPanel vm={vm} actions={actions} />
    </section>
  )
}
